import { Icon } from "@iconify/react";
import { LoginBox } from "../components/loginBox";
import { Header1, P } from "../components/headers";
import userBrowserLanguage from "../components/userBrowserLanguage";
import getTranslationJson from "../components/getTranslationJson";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components as sso } from "../hooks/sso";
import axios, { AxiosError, type AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import userTimeZoneCountryCode from "../components/userTimeZoneCountryCode";
import { InputPassword } from "../components/input";
import { Button } from "../components/button";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import LoadingCircle from "./loadingCircle";
import SvgIcon from "../components/SvgIcon";
import * as jose from "jose";
interface FormValues {
  new_password: string;
  confirm_password: string;
  apiError?: string;
  country_code?: string;
}

const parseJwt = (token: string): boolean => {
  try {
    const protectedHeader = jose.decodeProtectedHeader(token);
    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log(protectedHeader);
    }
    // const { payload, protectedHeader } = jose.jwtDecrypt(token, null, {
    //   issuer: "urn:example:issuer",
    //   audience: "urn:example:audience",
    // });
    // const claims = jose.decodeJwt(token);
    // console.log(payload);
    // if (payload.exp && payload.exp * 1000 < Date.now()) {
    //   console.log("Time Expired");
    //   return false;
    // }
    return true;
  } catch (e) {
    console.error("Invalid token", e);
    return false;
  }
};

function AuthPasswordResetConfirm() {
  const { token } = useParams();

  const translation = getTranslationJson(userBrowserLanguage());
  const countryCode = userTimeZoneCountryCode();

  const queryClient = useQueryClient();

  const [successfullNotification, setSuccessfullNotification] = useState(false);
  const [tokenValidCheck, setTokenValidCheck] = useState<"loading" | boolean>("loading");

  // JWT token check
  useEffect(() => {
    if (token) {
      setTokenValidCheck(parseJwt(token));
    } else {
      setTokenValidCheck(false);
    }
  }, [token]);

  // Mutations
  const mutationPasswordReset = useMutation({
    mutationKey: ["password_reset_confirm"],
    mutationFn: async (formData: FormValues): Promise<AxiosResponse<sso["schemas"]["ResetPasswordRequest"]>> => {
      setSuccessfullNotification(false);
      return axios.post(
        import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/reset-password/confirm/" + token,
        { new_password: formData.new_password, confirm_password: formData.confirm_password, country_code: countryCode },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["password_reset_confirm"] });
      setSuccessfullNotification(true);
    },
    onError: (data: AxiosError) => {
      // Invalidate and refetch
      console.error("Login error:", data);
      if (data.status === 400) {
        setError("apiError", {
          type: "custom",
          message:
            translation.error_invalid_token_or_expired ||
            "Invalid token or expired. Please trigger the password reset process again.",
        });
      } else {
        setError("apiError", {
          type: "custom",
          message: data.message || "An error occurred while logging in.",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    // control,
    formState: { errors },
    // getValues,
    // setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>();

  const passwordHandleSubmit = (form_data: FormValues) => {
    clearErrors(["apiError", "new_password", "confirm_password"]);
    // Password validation
    if (form_data.new_password.length < 8) {
      setError("new_password", {
        type: "manual",
        message: translation.error_password_too_short || "Password must be at least 8 characters.",
      });
      return;
    }
    if (form_data.new_password !== form_data.confirm_password) {
      setError("new_password", {
        type: "manual",
        message: translation.error_passwords_must_match || "The passwords must match.",
      });
      return;
    }
    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log(form_data);
      console.log("Time Zone Country Code:", countryCode);
      console.log("Browser Language:", userBrowserLanguage());
    }
    mutationPasswordReset.mutate({ ...form_data, country_code: countryCode });
  };

  if (tokenValidCheck === "loading") {
    return <LoadingCircle />;
  }

  if (tokenValidCheck === false) {
    return (
      <div className="font-inter-24">
        <LoginBox>
          <header className="fflex flex-col mb-6 items-center justify-center">
            <Header1>
              {translation.error_invalid_token_or_expired ||
                "Invalid token or expired. Please trigger the password reset process again."}
            </Header1>
            {/* <P>{translation.not_found_404_description || "Sorry, we couldn't find the page you were looking for."}</P> */}
          </header>
          <>
            <div className="flex items-center justify-center">
              <SvgIcon
                className=" text-7xl text-red-500 bg-red-50 rounded-[40px] mb-2 p-3 w-20 h-20"
                name="multiplication-sign"
              />
            </div>

            {/* <div className="text-sm text-gray-600 mb-4">
                {translation.email_verification_successfull_description ||
                  "You can now close this window and return to the login screen."}
              </div> */}
          </>
        </LoginBox>
      </div>
    );
  }
  return (
    <div className="font-inter-24">
      <LoginBox>
        <header className="fflex flex-col mb-6 items-center justify-center">
          <Header1>{translation.password_reset || "Password Reset"}</Header1>
          <P>
            {translation.password_reset_description || "Enter your new password below to reset your account password."}
          </P>
        </header>
        {errors.apiError && (
          <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
            {typeof errors.apiError.message === "string" ? errors.apiError.message : "An error occurred."}
          </div>
        )}
        {errors.new_password && errors.new_password.type !== "manual" && (
          <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
            {translation.error_required_field || "Required field"}
          </div>
        )}
        {errors.new_password && errors.new_password.type === "manual" && (
          <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
            {typeof errors.new_password.message === "string" ? errors.new_password.message : "An error occurred."}
          </div>
        )}
        <form onSubmit={handleSubmit(passwordHandleSubmit)}>
          <div className="flex flex-col gap-5">
            <input type="hidden" {...register("apiError")} />
            <InputPassword
              {...register("new_password", { required: true, min: 8 })}
              type="text"
              label={translation.field_password_new_label || "New password"}
              placeholder={translation.field_password_new_placeholder || "********"}
              name="new_password"
            />
            <InputPassword
              {...register("confirm_password", { required: true, min: 8 })}
              type="text"
              label={translation.field_password_confirm_label || "Confirm password"}
              placeholder={translation.field_password_confirm_placeholder || "********"}
              name="confirm_password"
            />
            <Button type="submit" disabled={mutationPasswordReset.isPending}>
              <Icon
                icon={mutationPasswordReset.isPending ? "prime:spinner" : "prime:send"}
                className={mutationPasswordReset.isPending ? "text-2xl mr-1 animate-spin" : "text-3xl mr-1 fill-white"}
              />
              {mutationPasswordReset.isPending
                ? `${translation.button_text_set_new_password || "Set New Password"}...`
                : translation.button_text_set_new_password || "Set New Password"}
            </Button>
          </div>
        </form>
        {successfullNotification && (
          <div className="my-5 text-sm text-brand-green bg-green-50 py-3 px-4 rounded-md font-medium text-center">
            {translation.notification_new_password_set_successfully || "New password set successfully."}
          </div>
        )}
        <div className="pb-4"></div>
      </LoginBox>
    </div>
  );
}

// function LinkAfterReset() {
//   const translation = getTranslationJson(userBrowserLanguage());
//   return (
//     <div>
//       <footer className="text-center text-sm text-slate-600 mt-7">
//         {translation.after_reset || "When you set your password, you can "}&nbsp;
//         <Link href="/auth/login">{translation.sign_in_here || "Sign in here"}</Link>
//       </footer>
//     </div>
//   );
// }

export default AuthPasswordResetConfirm;
